import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { RECIPE_PLANNING_BEHAVIORS } from '../utils/planningPreferences';

function RecipeForm({ recipe, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    title: recipe?.title || '',
    description: recipe?.description || '',
    totalTime: recipe?.totalTime || (recipe?.prepTime && recipe?.cookTime ? recipe.prepTime + recipe.cookTime : recipe?.totalTime || ''),
    servings: recipe?.servings || '',
    imageUrl: recipe?.imageUrl || '',
    ingredients: recipe?.ingredients || [{ name: '', quantity: '', unit: '' }],
    instructions: recipe?.instructions || [''],
    tags: recipe?.tags || [],
    suitableMealTypes: recipe?.suitableMealTypes || [],
    seasons: recipe?.seasons || [],
    planningBehavior: recipe?.planningBehavior || 'default',
    rating: recipe?.rating || '',
    nutrition: recipe?.nutrition || {
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
      fiber: '',
      sugar: '',
      sodium: ''
    }
  });
  
  // Store tags input as a string to allow typing commas
  const [tagsInput, setTagsInput] = useState(recipe?.tags?.join(', ') || '');
  
  // Track original servings for automatic adjustment when servings change
  const originalServingsRef = useRef(recipe?.servings || '');
  const [calculatingNutrition, setCalculatingNutrition] = useState(false);
  
  // Refs to track newly added fields for auto-focus
  const newIngredientIndexRef = useRef(null);
  const newInstructionIndexRef = useRef(null);
  
  // Refs for ingredient name inputs (first field in each ingredient row)
  const ingredientNameRefs = useRef({});
  
  // Refs for instruction textareas
  const instructionRefs = useRef({});
  
  // Auto-focus newly added ingredient name field
  useEffect(() => {
    if (newIngredientIndexRef.current !== null) {
      // Use setTimeout to ensure DOM is updated before focusing
      setTimeout(() => {
        const ref = ingredientNameRefs.current[newIngredientIndexRef.current];
        if (ref) {
          ref.focus();
          newIngredientIndexRef.current = null; // Reset after focusing
        }
      }, 0);
    }
  }, [formData.ingredients.length]);
  
  // Auto-focus newly added instruction textarea
  useEffect(() => {
    if (newInstructionIndexRef.current !== null) {
      // Use setTimeout to ensure DOM is updated before focusing
      setTimeout(() => {
        const ref = instructionRefs.current[newInstructionIndexRef.current];
        if (ref) {
          ref.focus();
          newInstructionIndexRef.current = null; // Reset after focusing
        }
      }, 0);
    }
  }, [formData.instructions.length]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    const fieldName = e.target.name;
    
    // If servings changed and we have nutrition data, adjust automatically
    if (fieldName === 'servings' && originalServingsRef.current && formData.nutrition) {
      const oldServings = parseFloat(originalServingsRef.current) || 1;
      const newServings = parseFloat(newValue) || 1;
      
      // Check if we have any nutrition values to adjust
      const hasNutritionData = formData.nutrition.calories || formData.nutrition.protein || 
                                formData.nutrition.carbs || formData.nutrition.fat;
      
      if (hasNutritionData && oldServings > 0 && newServings > 0 && oldServings !== newServings) {
        // Adjust nutrition proportionally
        const ratio = newServings / oldServings;
        const safeAdjustValue = (val, ratio) => {
          // Handle empty string, null, undefined
          if (val === '' || val === null || val === undefined) {
            return '';
          }
          const numVal = parseFloat(val);
          if (isNaN(numVal) || !isFinite(numVal)) {
            return '';
          }
          const result = numVal * ratio;
          if (isNaN(result) || !isFinite(result)) {
            return '';
          }
          return result;
        };
        
        setFormData({
          ...formData,
          servings: newValue,
          nutrition: {
            calories: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.calories, ratio);
              return adjusted === '' ? '' : Math.round(adjusted);
            })(),
            protein: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.protein, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })(),
            carbs: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.carbs, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })(),
            fat: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.fat, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })(),
            fiber: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.fiber, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })(),
            sugar: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.sugar, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })(),
            sodium: (() => {
              const adjusted = safeAdjustValue(formData.nutrition.sodium, ratio);
              return adjusted === '' ? '' : Math.round(adjusted * 10) / 10;
            })()
          }
        });
        // Update original servings reference
        originalServingsRef.current = newValue;
        return;
      }
    }
    
    // Update original servings reference when servings change (even if no adjustment)
    if (fieldName === 'servings') {
      originalServingsRef.current = newValue;
    }
    
    setFormData({ ...formData, [fieldName]: newValue });
  };

  const handleNutritionChange = (field, value) => {
    // Parse value and ensure it's not NaN
    let parsedValue = '';
    if (value && value !== '') {
      const num = parseFloat(value);
      if (!isNaN(num) && isFinite(num)) {
        parsedValue = num;
      }
    }
    
    setFormData({
      ...formData,
      nutrition: {
        ...formData.nutrition,
        [field]: parsedValue
      }
    });
    // If nutrition is manually entered, update original servings ref
    if (!originalServingsRef.current && formData.servings) {
      originalServingsRef.current = formData.servings;
    }
  };

  const handleCalculateNutrition = async () => {
    // Validate ingredients
    const validIngredients = formData.ingredients.filter(
      ing => ing.name && ing.name.trim() && ing.quantity && parseFloat(ing.quantity) > 0
    );

    if (validIngredients.length === 0) {
      alert('Please add at least one ingredient with a name and quantity before calculating nutrition.');
      return;
    }

    if (!formData.servings || parseFloat(formData.servings) <= 0) {
      alert('Please enter a valid number of servings before calculating nutrition.');
      return;
    }

    setCalculatingNutrition(true);
    try {
      const response = await axios.post(`/api/recipes/calculate-nutrition`, {
        ingredients: validIngredients.map(ing => ({
          name: ing.name.trim(),
          quantity: parseFloat(ing.quantity),
          unit: ing.unit.trim() || 'st.'
        })),
        servings: parseFloat(formData.servings)
      });

      if (response.data) {
        setFormData({
          ...formData,
          nutrition: {
            calories: response.data.calories || '',
            protein: response.data.protein || '',
            carbs: response.data.carbs || '',
            fat: response.data.fat || '',
            fiber: response.data.fiber || '',
            sugar: response.data.sugar || '',
            sodium: response.data.sodium || ''
          }
        });
        // Update original servings ref when nutrition is calculated
        originalServingsRef.current = formData.servings;
        alert('Nutrition calculated successfully!');
      }
    } catch (error) {
      console.error('Error calculating nutrition:', error);
      alert('Failed to calculate nutrition. Please try again or enter values manually.');
    } finally {
      setCalculatingNutrition(false);
    }
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = field === 'quantity' ? parseFloat(value) : value;
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const addIngredient = () => {
    const newIndex = formData.ingredients.length;
    newIngredientIndexRef.current = newIndex;
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { name: '', quantity: '', unit: '' }]
    });
  };

  const removeIngredient = (index) => {
    const newIngredients = formData.ingredients.filter((_, i) => i !== index);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData({ ...formData, instructions: newInstructions });
  };

  const addInstruction = () => {
    const newIndex = formData.instructions.length;
    newInstructionIndexRef.current = newIndex;
    setFormData({
      ...formData,
      instructions: [...formData.instructions, '']
    });
  };

  const removeInstruction = (index) => {
    const newInstructions = formData.instructions.filter((_, i) => i !== index);
    setFormData({ ...formData, instructions: newInstructions });
  };

  const handleTagChange = (e) => {
    // Store the raw input value so users can type commas
    const value = e.target.value;
    setTagsInput(value);
    // Process tags from the input
    const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData({ ...formData, tags });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Helper function to safely convert to number or undefined
    const safeNumber = (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) || !isFinite(num) ? undefined : num;
    };
    
    // Clean and convert data before submitting
    const submitData = {
      ...formData,
      // Convert totalTime and servings to numbers
      totalTime: formData.totalTime ? Number(formData.totalTime) : 0,
      servings: formData.servings ? Number(formData.servings) : 1,
      // Handle imageUrl: always include it if it exists (trimmed), preserve empty string if user cleared it
      // This ensures the backend receives the value and can preserve existing imageUrl if needed
      imageUrl: formData.imageUrl != null ? formData.imageUrl.trim() : undefined,
      // Convert rating to number or remove it if empty
      rating: formData.rating ? Number(formData.rating) : undefined,
      // Clean ingredients: filter out empty ones and convert quantity to number
      ingredients: formData.ingredients
        .filter(ing => ing.name && ing.name.trim() !== '')
        .map(ing => {
          const quantity = ing.quantity ? Number(ing.quantity) : 1;
          const unit = ing.unit ? ing.unit.trim() : '';
          // Ensure quantity is valid number
          if (isNaN(quantity) || quantity <= 0) {
            return null;
          }
          return {
            name: ing.name.trim(),
            quantity: quantity,
            unit: unit || 'st.' // Default unit if empty (Dutch: "stuk")
          };
        })
        .filter(ing => ing !== null), // Remove any null entries
      // Clean instructions: filter out empty ones
      instructions: formData.instructions
        .filter(inst => inst && inst.trim() !== '')
        .map(inst => inst.trim()),
      // Include suitableMealTypes (array, can be empty - will auto-detect if empty)
      suitableMealTypes: formData.suitableMealTypes || [],
      // Include seasons (array, can be empty - recipe available year-round if empty)
      seasons: formData.seasons || [],
      planningBehavior: formData.planningBehavior || 'default',
      // Clean nutrition: convert empty strings to undefined, numbers to numbers
      // Only include nutrition if at least one field has a value
      nutrition: (() => {
        const nutrition = {
          calories: safeNumber(formData.nutrition.calories),
          protein: safeNumber(formData.nutrition.protein),
          carbs: safeNumber(formData.nutrition.carbs),
          fat: safeNumber(formData.nutrition.fat),
          fiber: safeNumber(formData.nutrition.fiber),
          sugar: safeNumber(formData.nutrition.sugar),
          sodium: safeNumber(formData.nutrition.sodium)
        };
        // Check if any nutrition value exists
        const hasNutrition = Object.values(nutrition).some(val => val !== undefined);
        return hasNutrition ? nutrition : undefined;
      })()
    };
    
    // Validate that we have at least one ingredient and one instruction
    if (submitData.ingredients.length === 0) {
      alert('Please add at least one ingredient.');
      return;
    }
    
    if (submitData.instructions.length === 0) {
      alert('Please add at least one instruction.');
      return;
    }
    
    console.log('Prepared submit data:', submitData);
    try {
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting recipe:', error);
      // Don't show alert here - let the parent component handle it
      throw error; // Re-throw so parent can handle
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-2 pb-4 border-b">
        <button
          type="submit"
          className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md"
        >
          {recipe ? 'Save' : 'Create Recipe'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-stone-600 text-white rounded-lg font-semibold hover:bg-stone-700 transition-colors shadow-md"
          >
            Cancel
          </button>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
          rows="3"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Duration (min) *</label>
          <input
            type="number"
            name="totalTime"
            value={formData.totalTime}
            onChange={handleChange}
            required
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Servings *</label>
          <input
            type="number"
            name="servings"
            value={formData.servings}
            onChange={handleChange}
            required
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Image URL</label>
          <input
            type="text"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder="Enter image URL or local path"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Rating (1-5 stars)</label>
          <select
            name="rating"
            value={formData.rating}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded"
          >
            <option value="">No rating</option>
            <option value="1">1 star</option>
            <option value="2">2 stars</option>
            <option value="3">3 stars</option>
            <option value="4">4 stars</option>
            <option value="5">5 stars</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Ingredients *</label>
        {formData.ingredients.map((ingredient, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              ref={(el) => { ingredientNameRefs.current[index] = el; }}
              type="text"
              placeholder="Name"
              value={ingredient.name}
              onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded"
              required
            />
            <input
              type="number"
              placeholder="Quantity"
              value={ingredient.quantity}
              onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
              className="w-24 p-2 border border-gray-300 rounded"
              required
            />
            <input
              type="text"
              placeholder="Unit"
              value={ingredient.unit}
              onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
              className="w-24 p-2 border border-gray-300 rounded"
              required
            />
            <button
              type="button"
              onClick={() => removeIngredient(index)}
              className="px-4 py-2 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addIngredient}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 transition-colors shadow-md"
        >
          Add Ingredient
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Instructions *</label>
        {formData.instructions.map((instruction, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <textarea
              ref={(el) => { instructionRefs.current[index] = el; }}
              placeholder={`Step ${index + 1}`}
              value={instruction}
              onChange={(e) => handleInstructionChange(index, e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded"
              rows="2"
              required
            />
            <button
              type="button"
              onClick={() => removeInstruction(index)}
              className="px-4 py-2 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors shadow-md"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addInstruction}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 transition-colors shadow-md"
        >
          Add Step
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
        <input
          type="text"
          value={tagsInput}
          onChange={handleTagChange}
          placeholder="e.g., vegetarian, italian, healthy"
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Suitable for Meal Types</label>
        <p className="text-xs text-gray-500 mb-2">Select which meal types this recipe is suitable for. You can select multiple options.</p>
        <div className="flex flex-wrap gap-4">
          {['breakfast', 'lunch', 'dinner', 'snack'].map(mealType => (
            <label key={mealType} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.suitableMealTypes.includes(mealType)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      suitableMealTypes: [...formData.suitableMealTypes, mealType]
                    });
                  } else {
                    setFormData({
                      ...formData,
                      suitableMealTypes: formData.suitableMealTypes.filter(mt => mt !== mealType)
                    });
                  }
                }}
                className="mr-2"
              />
              <span className="capitalize">{mealType}</span>
            </label>
          ))}
        </div>
        {formData.suitableMealTypes.length === 0 && (
          <p className="text-xs text-gray-400 mt-1 italic">No meal types selected. The system will auto-detect based on recipe content.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Seasons</label>
        <p className="text-xs text-gray-500 mb-2">Select which seasons this recipe is best suited for. If none are selected, the recipe will be available year-round.</p>
        <div className="flex flex-wrap gap-4">
          {['spring', 'summer', 'fall', 'winter'].map(season => (
            <label key={season} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.seasons.includes(season)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      seasons: [...formData.seasons, season]
                    });
                  } else {
                    setFormData({
                      ...formData,
                      seasons: formData.seasons.filter(s => s !== season)
                    });
                  }
                }}
                className="mr-2"
              />
              <span className="capitalize">{season}</span>
            </label>
          ))}
        </div>
        {formData.seasons.length === 0 && (
          <p className="text-xs text-gray-400 mt-1 italic">No seasons selected. Recipe will be available year-round.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Planning behavior override</label>
        <p className="text-xs text-gray-500 mb-2">
          Override your profile defaults for how leftovers and repeats are handled for this recipe.
        </p>
        <select
          value={formData.planningBehavior}
          onChange={(e) => setFormData({ ...formData, planningBehavior: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded"
        >
          {RECIPE_PLANNING_BEHAVIORS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Nutritional Information (per serving)</h3>
          <button
            type="button"
            onClick={handleCalculateNutrition}
            disabled={calculatingNutrition}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {calculatingNutrition ? 'Calculating...' : 'Calculate Nutrition'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Calories</label>
            <input
              type="number"
              value={formData.nutrition.calories || ''}
              onChange={(e) => handleNutritionChange('calories', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Protein (g)</label>
            <input
              type="number"
              value={formData.nutrition.protein || ''}
              onChange={(e) => handleNutritionChange('protein', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Carbs (g)</label>
            <input
              type="number"
              value={formData.nutrition.carbs || ''}
              onChange={(e) => handleNutritionChange('carbs', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fat (g)</label>
            <input
              type="number"
              value={formData.nutrition.fat || ''}
              onChange={(e) => handleNutritionChange('fat', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fiber (g)</label>
            <input
              type="number"
              value={formData.nutrition.fiber || ''}
              onChange={(e) => handleNutritionChange('fiber', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sugar (g)</label>
            <input
              type="number"
              value={formData.nutrition.sugar || ''}
              onChange={(e) => handleNutritionChange('sugar', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sodium (mg)</label>
            <input
              type="number"
              value={formData.nutrition.sodium || ''}
              onChange={(e) => handleNutritionChange('sodium', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default RecipeForm;